import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import cn from 'classnames';
import type { MediaGalleryConfig } from '../../MediaGallery.config.ts';
import { useIntersection } from '@/widgets/hooks/useIntersection.tsx';
import type {
  GalleryEventSourceType,
  MediaGalleryItem
} from '../../MediaGallery.tsx';
import {
  getImageLazyLoadingProp,
  getNextNavigatableItemAndIndex,
  hasNext,
  hasPrev
} from '../../MediaGallery.utils.ts';
import styles from './Carousel.module.scss';
import Chevron from '../Chevron/Chevron.tsx';
import Thumbnails from '../Thumbnails/Thumbnails.tsx';
import CarouselSlide from '../CarouselSlide/CarouselSlide.tsx';
import { lastEventSource } from '@/widgets/hooks/useVideoTracking.tsx';

type LEFT = -1;
type RIGHT = 1;

const CONTROLS_UPDATE_DEBOUNCE_MS = 50;
const DRAG_VELOCITY = 3;
const MouseButtons = {
  Left: 0,
  Middle: 1,
  Right: 2,
  Back: 3,
  Forward: 4
} as const;

const getInitialCarouselState = () => ({
  /** Set to true when the mouse or drag event is 'down' */
  isDragStarts: false,
  /** Set to true when the mouse or drag event is 'move' */
  isDragging: false,
  /** This is the mouse down x-position when starting a new drag event */
  initialPosX: 0,
  /** This is the mouse down scroll left position of the carousel */
  initialScrollLeft: 0
});

export type CarouselProps = {
  items: MediaGalleryItem[];
  thumbnails?: MediaGalleryItem[];
  config: MediaGalleryConfig;
  onClick: (index: number) => void;
  onSlideChange?: (index: number) => void;
  onImageViewed?: (index: number, eventSource: GalleryEventSourceType) => void;
  class?: string;
};
function Carousel({
  items,
  thumbnails,
  config,
  class: className,
  onClick,
  onSlideChange,
  onImageViewed
}: CarouselProps): JSX.Element {
  const [currentIndex, setCurrentIndex] = useState(0);
  const {
    elementRef: carouselContainerRef,
    isVisible: containerElementIsVisible
  } = useIntersection<HTMLDivElement>({
    rootMargin: '0px 0px 0px 0px',
    threshold: 0.5
  });

  const {
    lazyLoading,
    translations: {
      imageLabel,
      prevLabel,
      nextLabel,
      noVideoSupportLabel,
      playPauseButtonLabel
    },
    loopVideos,
    hasReducedMotion
  } = config;
  const imageLoading = lazyLoading?.carousel ?? 'auto';
  /** The carousel container <div> element */
  const carouselWrapperRef = useRef<HTMLDivElement>(null);
  /** The carousel <ul> element inside the container */
  const carouselRef = useRef<HTMLUListElement>(null);
  /** Carousel state - using ref to avoid re-renders. @todo - abstract out to a signal? */
  const carouselState = useRef(getInitialCarouselState());
  const isSingleSlide = items.length === 1;

  /** Top-level handler for sliding the carousel to the slide-by-index */
  const setCurrentImageByIndex = (newIndex: number) => {
    if (!carouselRef.current) return;
    const slide = carouselRef.current.children[newIndex] as HTMLElement;

    slide.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
    setCurrentIndex(newIndex);
  };

  const calculateClampedIndex = () => {
    const { offsetWidth, scrollLeft } = carouselRef.current!;
    if (!scrollLeft) return 0;
    return Math.max(
      0,
      Math.min(Math.round(scrollLeft / offsetWidth), items.length - 1)
    );
  };

  const handleSlideDragDown = (event: MouseEvent) => {
    if (!carouselRef.current) return;

    const carousel = carouselRef.current;

    carouselState.current = {
      isDragStarts: true,
      isDragging: false,
      initialPosX: event.pageX - carousel.offsetLeft,
      initialScrollLeft: carousel.scrollLeft
    };
  };

  const handleSlideDragMove = (event: MouseEvent) => {
    if (!carouselRef.current || !carouselState.current.isDragStarts) return;
    event.preventDefault();
    const carousel = carouselRef.current;
    const carouselX = event.pageX - carousel.offsetLeft;
    const { initialPosX, initialScrollLeft } = carouselState.current;

    carouselState.current = {
      isDragStarts: true,
      isDragging: true,
      initialPosX,
      initialScrollLeft
    };

    carouselRef.current.classList.add(styles.dragging);

    // Walk to the slided position
    carousel.scrollLeft =
      initialScrollLeft - (carouselX - initialPosX) * DRAG_VELOCITY;
  };

  /** Handle drag-release events and decide which slide to slide to */
  const handleSlideDragEnd = (event: MouseEvent) => {
    const { isDragging } = carouselState.current;

    // Handle onClick before resetting the carousel state
    if (!isDragging && event.button === MouseButtons.Left) {
      onClick(calculateClampedIndex());
    }

    handleSlideDragLeave(event);
  };

  /** Fires when mouse/drag leaves the carousel frame */
  const handleSlideDragLeave = (event: MouseEvent) => {
    const { isDragging } = carouselState.current;

    // Reset the carousel state also without dragging (on click)
    carouselState.current = getInitialCarouselState();

    if (!carouselRef.current || !isDragging) return;

    event.preventDefault();
    event.stopPropagation();
    setCurrentImageByIndex(calculateClampedIndex());
    carouselRef.current.classList.remove(styles.dragging);
  };

  const navigateBy = (
    event: JSX.TargetedEvent<HTMLButtonElement>,
    direction: LEFT | RIGHT,
    eventSource: GalleryEventSourceType
  ) => {
    event.preventDefault();
    if (!carouselRef.current) return;

    const { newIndex, newItem } = getNextNavigatableItemAndIndex(
      items ?? [],
      currentIndex,
      direction
    );

    if (newItem) {
      setCurrentImageByIndex(newIndex);
    }

    onImageViewed?.(newIndex, eventSource);
  };

  const handleThumbnailClick = (
    _event: JSX.TargetedEvent<HTMLButtonElement>,
    clickedIndex: number
  ) => {
    if (clickedIndex === currentIndex) {
      return;
    }
    setCurrentImageByIndex(clickedIndex);
    onImageViewed?.(clickedIndex, 'thumbnail');
  };

  useEffect(() => {
    const controller = new AbortController();
    const ul = carouselRef.current;
    let timeout: ReturnType<typeof setTimeout>;

    const updateControlsOnScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setCurrentIndex((index) => {
          const activeIndex = Math.round(ul!.scrollLeft / ul!.offsetWidth);

          if (index !== activeIndex) {
            onImageViewed?.(activeIndex, 'default');
            onSlideChange?.(activeIndex);
          }

          return activeIndex;
        });
      }, CONTROLS_UPDATE_DEBOUNCE_MS);
    };

    // trigger a single view event on first load
    onImageViewed?.(currentIndex, 'default');
    ul?.addEventListener('scroll', updateControlsOnScroll, {
      passive: true,
      signal: controller.signal
    });

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, []);

  const arrowPrevIsEnabled = hasPrev(items, currentIndex);
  const arrowNextIsEnabled = hasNext(items, currentIndex);
  const hasThumbnails = !config.disabledCarouselThumbnails;

  return (
    <>
      <div class={cn(styles.container, className)} ref={carouselContainerRef}>
        <div class={styles.wrapper} ref={carouselWrapperRef}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <ul
            class={styles.list}
            ref={carouselRef}
            onMouseDown={handleSlideDragDown}
            onMouseMove={handleSlideDragMove}
            onMouseUp={handleSlideDragEnd}
            onMouseLeave={handleSlideDragLeave}
          >
            {items.map((item, index) => {
              // eager load the first image and lazy load the rest
              // @todo - what about galleries which are not in the fold?
              const loading =
                index === 0
                  ? 'eager'
                  : getImageLazyLoadingProp(imageLoading, index, 0);
              return (
                <CarouselSlide
                  item={item}
                  isCurrentSlide={index === currentIndex}
                  loading={loading}
                  a11yLabel={imageLabel}
                  noVideoSupportLabel={noVideoSupportLabel}
                  playPauseButtonLabel={playPauseButtonLabel}
                  loopVideos={loopVideos}
                  carouselInFold={containerElementIsVisible}
                  hasReducedMotion={hasReducedMotion}
                />
              );
            })}
          </ul>

          {!isSingleSlide && (
            <div class={styles.chevronsBox}>
              <Chevron
                type="prev"
                onClick={(event) => {
                  lastEventSource.value = 'arrow scroll';
                  navigateBy(event, -1, 'chevron');
                }}
                a11yLabel={prevLabel}
                disabled={!arrowPrevIsEnabled}
              />
              <Chevron
                type="next"
                onClick={(event) => {
                  lastEventSource.value = 'arrow scroll';
                  navigateBy(event, 1, 'chevron');
                }}
                a11yLabel={nextLabel}
                disabled={!arrowNextIsEnabled}
              />
            </div>
          )}
        </div>
      </div>
      {hasThumbnails && (
        <Thumbnails
          config={config}
          items={thumbnails || items}
          selectedIndex={currentIndex}
          onThumbnailClick={(e, clickedIndex) =>
            handleThumbnailClick(e, clickedIndex)
          }
        />
      )}
    </>
  );
}

export default Carousel;
